import React, { useEffect, useState } from "react";
import { addPasskey, defaultPasskeyName, deletePasskey, getPasskeys, PasskeyInfo, passkeysSupported } from "../../api/PasskeyApi";
import ContainerCard from "../../common/CardContainer";
import { toast } from "../../common/notify";

function formatDate(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** My Details: add, see and remove passkeys for signing in without a password. */
function PasskeysCard() {
  const supported = passkeysSupported();
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null);
  const [error, setError] = useState("");
  const [name, setName] = useState(defaultPasskeyName());
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getPasskeys()
      .then(setPasskeys)
      .catch((e: Error) => setError(e.message));
  }, []);

  async function onAdd() {
    setAdding(true);
    setError("");
    try {
      const passkey = await addPasskey(name);
      setPasskeys((list) => [...(list ?? []), passkey]);
      toast.success(`Passkey "${passkey.name}" added. You can now sign in with it.`);
    } catch (e: any) {
      setError((e as Error).message);
    } finally {
      setAdding(false);
    }
  }

  async function onDelete(passkey: PasskeyInfo) {
    if (!window.confirm(`Remove the passkey "${passkey.name}"?\n\nYou won't be able to sign in with it any more. Also delete it from the device's password manager if you no longer need it.`)) {
      return;
    }
    try {
      await deletePasskey(passkey.id);
      setPasskeys((list) => (list ?? []).filter((p) => p.id !== passkey.id));
      toast.success(`Passkey "${passkey.name}" removed`);
    } catch (e: any) {
      toast.error(`Could not remove the passkey: ${(e as Error).message}`);
    }
  }

  return (
    <ContainerCard heading="Passkeys">
      <p>
        A passkey lets you sign in with your fingerprint, face or device PIN instead of a password. It's stored on your device (or synced by your phone or browser),
        and only works on this site.
      </p>
      {passkeys === null && !error ? <p className="text-muted">Loading...</p> : null}
      {passkeys !== null && passkeys.length === 0 ? <p className="text-muted">You haven't added a passkey yet.</p> : null}
      {passkeys !== null && passkeys.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Added</th>
                <th>Last used</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {passkeys.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.name}
                    {p.isBackedUp ? <span className="text-muted small"> · synced</span> : null}
                  </td>
                  <td>{formatDate(p.createdAt)}</td>
                  <td>{formatDate(p.lastUsedAt)}</td>
                  <td className="text-end">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(p)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {supported ? (
        <div className="d-flex flex-wrap align-items-end gap-2">
          <div>
            <label htmlFor="passkeyName" className="form-label">
              Name for this passkey
            </label>
            <input id="passkeyName" type="text" className="form-control" maxLength={60} value={name} disabled={adding} onChange={(e) => setName(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={adding} onClick={onAdd}>
            {adding ? "Follow your device's prompts..." : "Add a passkey"}
          </button>
        </div>
      ) : (
        <p className="text-muted mb-0">This browser doesn't support passkeys.</p>
      )}
      {error ? <div className="alert alert-danger py-2 mt-2 mb-0">{error}</div> : null}
    </ContainerCard>
  );
}

export default PasskeysCard;
